import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'

const STORAGE_PREFIX = 'mock-vite:ui-state:'

export function usePersistentState<T>(
  key: string,
  initialValue: T | (() => T),
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const getInitialValue = useCallback(
    () => (typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue),
    [initialValue],
  )

  const storageKey = `${STORAGE_PREFIX}${key}`
  const [value, setValue] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(storageKey)
      return storedValue === null ? getInitialValue() : JSON.parse(storedValue)
    } catch {
      return getInitialValue()
    }
  })

  const setPersistentValue: Dispatch<SetStateAction<T>> = useCallback(
    (nextValue) => {
      setValue((currentValue) => {
        const resolvedValue =
          typeof nextValue === 'function'
            ? (nextValue as (previousValue: T) => T)(currentValue)
            : nextValue

        try {
          localStorage.setItem(storageKey, JSON.stringify(resolvedValue))
        } catch {
          // Keep the in-memory state usable when storage is unavailable.
        }

        return resolvedValue
      })
    },
    [storageKey],
  )

  const clearPersistentValue = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // Keep the reset action usable when storage is unavailable.
    }
    setValue(getInitialValue())
  }, [getInitialValue, storageKey])

  return [value, setPersistentValue, clearPersistentValue]
}
